import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Plus, Trash2 } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { getSubscriptions, createSubscription, deleteSubscription } from '../api/buzz'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { Subscription } from '../types'

export function Subscriptions() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')

  const { data: subs = [], isLoading } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => getSubscriptions().then((r) => r.data),
  })

  const filtered = subs.filter((s: Subscription) =>
    [s.subscriberId, s.eventTitle, s.callbackUrl].some((v) =>
      v.toLowerCase().includes(search.toLowerCase())
    )
  )

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ subscriberId: '', eventTitle: '', callbackUrl: '' })
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: () => createSubscription(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
      setShowCreate(false)
      setForm({ subscriberId: '', eventTitle: '', callbackUrl: '' })
    },
  })

  const [deleteTarget, setDeleteTarget] = useState<Subscription | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['subscriptions'] }); setDeleteTarget(null) },
  })

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Subscriptions</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Manage event subscriptions and callbacks</p>
        </div>
        <Button onClick={() => setShowCreate(true)} icon={Plus}>
          New subscription
        </Button>
      </div>

      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Search subscriptions" />
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-neutral-400">Loading</div>
        ) : subs.length === 0 ? (
          <EmptyState icon={Bell} title="No subscriptions yet" description="Create a subscription to start receiving event callbacks" />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Bell} title="No results" description={`No subscriptions match "${search}"`} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Subscriber ID</th>
                <th>Event</th>
                <th>Callback URL</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s: Subscription) => (
                <tr key={s.id}>
                  <td className="font-mono text-xs text-neutral-700">{s.subscriberId}</td>
                  <td className="text-neutral-600">{s.eventTitle}</td>
                  <td className="font-mono text-xs text-neutral-500 truncate max-w-xs">{s.callbackUrl}</td>
                  <td className="text-neutral-400">{fmt(s.createdAt)}</td>
                  <td>
                    <button onClick={() => setDeleteTarget(s)} className="text-neutral-300 hover:text-red-500 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="New subscription" onClose={() => setShowCreate(false)} size="sm">
          <div className="space-y-4">
            {[
              { key: 'subscriberId', label: 'Subscriber ID *', placeholder: 'notification-service' },
              { key: 'eventTitle', label: 'Event title *', placeholder: 'order.created' },
              { key: 'callbackUrl', label: 'Callback URL *', placeholder: 'https://service/webhook' },
            ].map(({ key, label, placeholder }) => (
              <Field key={key} label={label}>
                <input value={form[key as keyof typeof form]} onChange={set(key as keyof typeof form)} placeholder={placeholder}
                  className="field" />
              </Field>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={!form.subscriberId || !form.eventTitle || !form.callbackUrl || create.isPending}>
                {create.isPending ? 'Creating' : 'Create'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete subscription"
          message={`Delete subscription for "${deleteTarget.eventTitle}"?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}
